import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Box, Heading, Stack } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/hooks/use-auth';
import useIsViewer from '@/lib/hooks/use-is-viewer';
import usePlannerPlansStore, { personalPlanOf, workspacePlanOf } from '@/utils/use-planner-plans';
import { PlannerPlan } from '@/lib/types';
import { blankDraft } from './draft';
import { initialPlannerScreen, plannerReducer } from './planner-machine';
import CalendarStripCard from './calendar/calendar-strip-card';
import WorkingPanelCard from './panel/working-panel-card';

// Planner shell: hosts the calendar strip and the working panel, and owns the
// ephemeral screen state machine (per-visit UI state — not a store). The
// route already keys this component on workspaceId (see PlannerRoute in
// app.tsx), so switching workspaces remounts the whole tree.
export default function Planner() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const { workspace } = useAuth();
  const isViewer = useIsViewer();
  const [screen, dispatch] = useReducer(plannerReducer, initialPlannerScreen);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const plansByDate = usePlannerPlansStore((s) => s.plansByDate);
  const fetchByDate = usePlannerPlansStore((s) => s.fetchByDate);
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedDateParam = useRef(false);
  const didInitDefault = useRef(false);

  const selectDateAction = useCallback(
    (date: string, dayPlans: PlannerPlan[]) =>
      ({
        type: 'SELECT_DATE',
        date,
        hasWorkspacePlan: Boolean(workspacePlanOf(dayPlans)),
        hasPersonalPlan: Boolean(personalPlanOf(dayPlans)),
        isViewer,
      }) as const,
    [isViewer],
  );

  const handleSelectDate = useCallback(
    (date: string) => {
      setSelectedDate(date);
      dispatch(selectDateAction(date, plansByDate()[date] ?? []));
    },
    [plansByDate, selectDateAction],
  );

  // Deep-link support: preselect the ?date=YYYY-MM-DD from the URL on mount
  // (Share links, home-page quick actions). Uses a direct fetchByDate call
  // (rather than the calendar strip's week/month-scoped cache) so a shared
  // link resolves correctly even when the linked date falls outside the
  // strip's default (today-centered) load window — e.g. a plan shared from
  // three months ago. Consumed exactly once per mount, then stripped from
  // the URL. With &build=1 (home's "Build a plan" quick action), a date
  // without a plan skips the choice screen and opens the builder directly
  // with a blank draft; a date that already has a plan falls back to the
  // view screen so the deep-link can't create a duplicate.
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (!dateParam || consumedDateParam.current || !workspaceId) return;
    consumedDateParam.current = true;
    const wantsBuilder = searchParams.get('build') === '1' && !isViewer;
    setSelectedDate(dateParam);
    fetchByDate(workspaceId, dateParam)
      .then((plans) => {
        if (plans.length === 0 && wantsBuilder) {
          dispatch({ type: 'CHOOSE_CREATE_NEW', draft: blankDraft(workspace?.id ?? 0, dateParam) });
          return;
        }
        dispatch(selectDateAction(dateParam, plans));
      })
      .catch(() => {
        // fetchByDate already surfaces a toaster-visible error via the store;
        // fall back to the choice/empty screen so the UI isn't left stuck loading.
        dispatch(selectDateAction(dateParam, []));
      });
    const next = new URLSearchParams(searchParams);
    next.delete('date');
    next.delete('build');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, workspaceId, isViewer]);

  // Default selection: preselect today on load so the planner opens with a
  // date in focus rather than the empty "select a date" state. Runs exactly
  // once per mount (the ref latches on first run, even when it defers) and
  // yields to a ?date= deep-link when one is present. Resolves hasPlan via a
  // direct fetchByDate — same rationale as the deep-link effect above: it
  // avoids racing the calendar strip's async range load.
  useEffect(() => {
    if (didInitDefault.current || !workspaceId) return;
    didInitDefault.current = true;
    if (searchParams.get('date')) return; // deep-link wins
    const today = format(new Date(), 'yyyy-MM-dd');
    setSelectedDate(today);
    fetchByDate(workspaceId, today)
      .then((plans) => {
        dispatch(selectDateAction(today, plans));
      })
      .catch(() => {
        dispatch(selectDateAction(today, []));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, isViewer]);

  return (
    <Box mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }} pb={10}>
      <Heading as='h1' textStyle='display' fontSize={{ base: 'h2', md: 'h1' }} mt={3} mb={5}>
        Planner
      </Heading>

      <Stack gap={4}>
        {workspaceId && (
          <CalendarStripCard
            workspaceId={workspaceId}
            selectedDate={selectedDate}
            onSelectDate={handleSelectDate}
          />
        )}

        <WorkingPanelCard screen={screen} workspaceId={workspace?.id ?? 0} dispatch={dispatch} />
      </Stack>
    </Box>
  );
}
